import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import styles from './Components.module.css'

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`
const pct = (n) => `${Number(n || 0).toFixed(1)}%`

const COLORS = ['#3b82f6', '#48c78e', '#a78bfa', '#ffb800', '#f87171']

function getCalc(dato) {
    return dato?.comision_calculada || {}
}

function getComponente(dato, key) {
    return Number(getCalc(dato).componentes?.[key]?.valor_cop || 0)
}

function KpiCard({ label, value, hint, tone = 'default' }) {
    return (
        <div className={`${styles.kpiCard} ${styles[`kpiTone_${tone}`] || ''}`}>
            <span className={styles.kpiLabel}>{label}</span>
            <strong className={styles.kpiValue}>{value}</strong>
            {hint && <small className={styles.kpiHint}>{hint}</small>}
        </div>
    )
}

function EmptyChart() {
    return <div className={styles.kpiEmptyChart}>Sin datos calculados para graficar</div>
}

function KpiPanel({ planes, datos, periodo }) {
    const datosConCalculo = datos.filter(d => getCalc(d).total_cop != null)
    const totalVentas = datos.reduce((acc, d) => acc + Number(d.ventas_periodo || 0), 0)
    const totalRecaudo = datos.reduce((acc, d) => acc + Number(d.recaudo_periodo || 0), 0)
    const totalComision = datosConCalculo.reduce((acc, d) => acc + Number(getCalc(d).total_cop || 0), 0)
    const totalVentaComision = datosConCalculo.reduce((acc, d) => acc + getComponente(d, 'venta'), 0)
    const totalRecaudoComision = datosConCalculo.reduce((acc, d) => acc + getComponente(d, 'recaudo'), 0)
    const totalClientes = datos.reduce((acc, d) => acc + Number(d.nuevos_clientes || 0), 0)
    const totalComodatos = datos.reduce((acc, d) => acc + Number(d.nuevos_comodatos || 0), 0)
    const cobertura = planes.length > 0 ? (datos.length / planes.length) * 100 : 0
    const aprobados = datos.filter(d => d.estado === 'aprobado').length
    const pendientes = datos.filter(d => ['borrador', 'enviado'].includes(d.estado)).length
    const promedioCumplimiento = datosConCalculo.length > 0
        ? datosConCalculo.reduce((acc, d) => acc + Number(getCalc(d).pct_venta_meta || 0), 0) / datosConCalculo.length
        : 0
    const tasaComision = totalVentas > 0 ? (totalComision / totalVentas) * 100 : 0

    const byGestor = planes.map(plan => {
        const dato = datos.find(d => d.gestor_email === plan.gestor_email)
        const calc = getCalc(dato)
        return {
            nombre: plan.gestor_nombre || plan.gestor_email,
            email: plan.gestor_email,
            ventas: Number(dato?.ventas_periodo || 0),
            recaudo: Number(dato?.recaudo_periodo || 0),
            comision: Number(calc.total_cop || 0),
            cumplimiento: Number(calc.pct_venta_meta || 0),
            estado: dato?.estado || 'sin_datos',
        }
    })

    const ranking = [...byGestor]
        .filter(g => g.comision > 0 || g.ventas > 0)
        .sort((a, b) => b.comision - a.comision)
        .slice(0, 8)

    const cumplimientoData = [...byGestor]
        .filter(g => g.ventas > 0 || g.cumplimiento > 0)
        .sort((a, b) => b.cumplimiento - a.cumplimiento)
        .slice(0, 8)
        .map(g => ({ ...g, nombreCorto: g.nombre.split(' ').slice(0, 2).join(' ') }))

    const distribucion = [
        { name: 'Venta', value: totalVentaComision },
        { name: 'Recaudo', value: totalRecaudoComision },
        { name: 'Otros', value: Math.max(totalComision - totalVentaComision - totalRecaudoComision, 0) },
    ].filter(item => item.value > 0)

    const estadoData = [
        { label: 'Aprobado', value: aprobados },
        { label: 'Pendiente', value: pendientes },
        { label: 'Sin datos', value: Math.max(planes.length - datos.length, 0) },
    ]

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <div>
                    <h2 className={styles.sectionTitle}>Tablero KPI - {periodo}</h2>
                    <p className={styles.sectionSub}>Indicadores consolidados del proceso de comisiones</p>
                </div>
            </div>

            <div className={styles.kpiGrid}>
                <KpiCard label="Comision total" value={fmt(totalComision)} hint={`${datosConCalculo.length} gestores calculados`} tone="green" />
                <KpiCard label="Ventas registradas" value={fmt(totalVentas)} hint={`Tasa comision ${pct(tasaComision)}`} tone="blue" />
                <KpiCard label="Recaudo registrado" value={fmt(totalRecaudo)} hint={`${fmt(totalRecaudoComision)} en comision`} tone="purple" />
                <KpiCard label="Cobertura datos" value={pct(cobertura)} hint={`${datos.length} de ${planes.length} planes`} tone="amber" />
                <KpiCard label="Cumplimiento promedio" value={pct(promedioCumplimiento)} hint="Sobre gestores con calculo" tone="blue" />
                <KpiCard label="Clientes / comodatos" value={`${totalClientes} / ${totalComodatos}`} hint="Nuevos registros del periodo" tone="green" />
            </div>

            <div className={styles.kpiChartsGrid}>
                <div className={styles.kpiPanel}>
                    <div className={styles.kpiPanelHeader}>
                        <h3>Distribucion de comisiones</h3>
                        <span>{fmt(totalComision)}</span>
                    </div>
                    {distribucion.length === 0 ? <EmptyChart /> : (
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={distribucion} dataKey="value" nameKey="name" innerRadius={58} outerRadius={92} paddingAngle={3}>
                                    {distribucion.map((entry, index) => (
                                        <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => fmt(value)} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                    <div className={styles.kpiLegend}>
                        {distribucion.map((item, index) => (
                            <span key={item.name}>
                                <i style={{ background: COLORS[index % COLORS.length] }} />
                                {item.name}: {fmt(item.value)}
                            </span>
                        ))}
                    </div>
                </div>

                <div className={styles.kpiPanel}>
                    <div className={styles.kpiPanelHeader}>
                        <h3>Top comisiones</h3>
                        <span>{ranking.length} gestores</span>
                    </div>
                    {ranking.length === 0 ? <EmptyChart /> : (
                        <div className={styles.kpiRanking}>
                            {ranking.map((g, index) => (
                                <div key={g.email} className={styles.kpiRankRow}>
                                    <span className={styles.kpiRankIndex}>{index + 1}</span>
                                    <div>
                                        <strong>{g.nombre}</strong>
                                        <small>{fmt(g.ventas)} ventas</small>
                                    </div>
                                    <b>{fmt(g.comision)}</b>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.kpiChartsGrid}>
                <div className={styles.kpiPanel}>
                    <div className={styles.kpiPanelHeader}>
                        <h3>Cumplimiento ventas</h3>
                        <span>Top 8</span>
                    </div>
                    {cumplimientoData.length === 0 ? <EmptyChart /> : (
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={cumplimientoData} margin={{ top: 12, right: 10, left: -18, bottom: 0 }}>
                                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                                <XAxis dataKey="nombreCorto" tick={{ fill: '#64748b', fontSize: 11 }} interval={0} height={48} />
                                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(value) => `${value}%`} />
                                <Tooltip formatter={(value) => pct(value)} />
                                <Bar dataKey="cumplimiento" radius={[6, 6, 0, 0]} fill="#3b82f6" />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>

                <div className={styles.kpiPanel}>
                    <div className={styles.kpiPanelHeader}>
                        <h3>Estado del proceso</h3>
                        <span>{planes.length} planes</span>
                    </div>
                    <div className={styles.kpiStatusList}>
                        {estadoData.map((item, index) => {
                            const ratio = planes.length > 0 ? (item.value / planes.length) * 100 : 0
                            return (
                                <div key={item.label} className={styles.kpiStatusItem}>
                                    <div>
                                        <span>{item.label}</span>
                                        <strong>{item.value}</strong>
                                    </div>
                                    <div className={styles.kpiStatusTrack}>
                                        <div style={{ width: `${Math.min(ratio, 100)}%`, background: COLORS[index % COLORS.length] }} />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default KpiPanel
